import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { useSilver } from "@/context/SilverContext";
import { COMPILATION_STAGES } from "@/data/content";
import { AnimatedNumber } from "@/components/silver/common/AnimatedNumber";
import { CompilerVisual } from "@/components/silver/tool/CompilerVisual";
import { DataTicker } from "@/components/silver/tool/DataTicker";
import { cn } from "@/lib/utils";

const areaSummary = (areas) => (areas.length <= 4 ? areas.join(" + ") : `${areas.slice(0, 3).join(" + ")} + ${areas.length - 3} more`);

function StageList({ stage }) {
  return (
    <ol className="space-y-3.5" data-testid="compilation-stages">
      {COMPILATION_STAGES.map((label, i) => {
        const done = stage > i;
        const active = stage === i;
        return (
          <li key={label} className={cn("flex items-center gap-3 font-ui text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-500", done ? "text-white/70" : active ? "text-[#C6EE5A]" : "text-white/25")}>
            <span className={cn("grid h-5 w-5 place-items-center rounded-full border transition-all duration-500", done ? "border-[#96C11F] bg-[#96C11F] text-[#12290F]" : active ? "border-[#C6EE5A]" : "border-white/15")}>
              {done ? <Check className="h-3 w-3" strokeWidth={3} /> : active ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            </span>
            {label}
          </li>
        );
      })}
    </ol>
  );
}

function Stat({ label, value, show, testId }) {
  return (
    <div className="border-t border-white/10 pt-3">
      <AnimatedNumber value={show ? value : 0} duration={0.9} className="num block text-2xl font-extrabold tracking-[-0.03em] text-white" data-testid={testId} />
      <span className="mt-0.5 block font-ui text-[10px] uppercase tracking-[0.14em] text-white/45">{label}</span>
    </div>
  );
}

export function CompilationOverlay({ onComplete }) {
  const { audience } = useSilver();
  const snapshot = useMemo(() => audience, []); // eslint-disable-line react-hooks/exhaustive-deps
  const reduce = useReducedMotion();
  const [stage, setStage] = useState(0);
  const pctRef = useRef(null);
  const barRef = useRef(null);
  const doneRef = useRef(onComplete);
  doneRef.current = onComplete;
  const stageMs = reduce ? 220 : 1300;
  const total = stageMs * COMPILATION_STAGES.length;
  const hold = reduce ? 600 : 1700;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const el = now - start;
      const p = Math.min(1, el / total);
      // Ease that lingers through the final stretch, so 100% lands with the last stage rather than racing ahead.
      const eased = p < 1 ? Math.pow(0.5 - Math.cos(Math.PI * p) / 2, 1.25) : 1;
      if (pctRef.current) pctRef.current.textContent = `${Math.round(eased * 100)}`;
      if (barRef.current) barRef.current.style.transform = `scaleX(${eased})`;
      const s = Math.min(COMPILATION_STAGES.length, Math.floor(el / stageMs));
      setStage(s);
      if (el < total + hold) raf = requestAnimationFrame(tick);
      else doneRef.current();
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      document.body.style.overflow = "";
    };
  }, [stageMs, total, hold]);

  const done = stage >= COMPILATION_STAGES.length;

  return (
    <motion.div role="dialog" aria-modal="true" aria-label="Building your audience"
      className="fixed inset-0 z-[60] overflow-y-auto bg-[#0B1C0D]"
      initial={{ clipPath: "circle(0% at 50% 60%)" }} animate={{ clipPath: "circle(150% at 50% 60%)" }} exit={{ clipPath: "circle(0% at 50% 38%)", transition: { duration: 1.05, ease: [0.7, 0, 0.3, 1] } }}
      transition={{ duration: 0.9, ease: [0.7, 0, 0.3, 1] }} data-testid="compilation-overlay">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true"
        style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[900px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(150,193,31,0.16),transparent_60%)]" aria-hidden="true" />

      <div className="relative mx-auto grid min-h-full max-w-7xl grid-cols-1 items-center gap-8 px-5 py-10 sm:px-8 lg:grid-cols-[1fr_minmax(0,560px)_1fr] lg:gap-10">
        <div className="order-2 space-y-8 lg:order-1">
          <div>
            <p className="eyebrow">Analysing your patch</p>
            <p className="mt-3 text-[15px] text-white/60">Compiling audience for <span className="font-semibold text-white">{areaSummary(snapshot.areas)}</span></p>
          </div>
          <div>
            <p className="flex items-start font-ui font-light tracking-[-0.05em] text-white" style={{ fontSize: "clamp(4rem, 8vw, 7rem)", lineHeight: 0.9 }}>
              <span ref={pctRef} className="num" data-testid="compilation-percent">0</span>
              <span className="mt-2 text-[0.35em] text-[#C6EE5A]">%</span>
            </p>
            <div className="mt-5 h-px w-full overflow-hidden bg-white/10">
              <div ref={barRef} className="h-full origin-left bg-gradient-to-r from-[#7AA818] to-[#C6EE5A]" style={{ transform: "scaleX(0)" }} />
            </div>
          </div>
          <div className="hidden sm:block"><StageList stage={stage} /></div>
          <p className="sm:hidden font-ui text-[11px] font-semibold uppercase tracking-[0.14em] text-[#C6EE5A]" aria-live="polite">
            {done ? "Audience ready" : COMPILATION_STAGES[stage]}
          </p>
        </div>

        <div className="relative order-1 mx-auto aspect-square w-full max-w-[300px] sm:max-w-[440px] lg:order-2 lg:max-w-[560px]">
          <CompilerVisual stage={stage} audience={snapshot} />
          {done && (
            <motion.div className="absolute inset-x-0 bottom-[-4%] text-center" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
              <h2 className="text-3xl font-extrabold tracking-[-0.04em] text-white sm:text-4xl" data-testid="audience-ready-heading">Audience ready.</h2>
            </motion.div>
          )}
        </div>

        <div className="order-3 space-y-6">
          <div className="grid grid-cols-2 gap-5">
            <Stat label="Postcode districts" value={snapshot.areas.length} show={stage >= 0} testId="compilation-stat-districts" />
            <Stat label="Audience profiles" value={snapshot.profileCount} show={stage >= 2} testId="compilation-stat-profiles" />
            <Stat label="Homeowner households mapped" value={snapshot.silverTotal} show={stage >= 1} testId="compilation-stat-silver" />
            <Stat label="Matching households" value={snapshot.total} show={stage >= 4} testId="compilation-stat-matching" />
          </div>
          <DataTicker audience={snapshot} running={stage >= 1 && stage <= 4} />
          <p className="font-ui text-[10.5px] leading-relaxed text-white/35">
            Processing the homeowner audience dataset for your selected districts. Counts are audience estimates, not indications that any household is selling.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
